var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import isPropValid from '@emotion/is-prop-valid';
import { focusOutline, typography } from '../../../../styles/style-mixins';
import SortArrow from '../SortArrow';
import styled from '../../../../styles/styled';
import { AccessibilityError } from '../../../../util/errors';
const baseStyles = ({ theme, align }) => css `
  background-color: var(--cui-bg-normal);
  border-bottom: ${theme.borderWidth.kilo} solid var(--cui-border-divider);
  padding: ${theme.spacings.giga};
  text-align: ${align};
  transition: background-color ${theme.transitions.default},
    color ${theme.transitions.default};
`;
const hoveredStyles = ({ isHovered }) => isHovered &&
    css `
    background-color: var(--cui-bg-normal-hovered);
  `;
const colStyles = ({ theme, scope }) => scope === 'col' &&
    css `
    ${typography('two')(theme)};
    color: var(--cui-fg-subtle);
    font-weight: ${theme.fontWeight.bold};
    padding: ${theme.spacings.byte} ${theme.spacings.giga};
    vertical-align: middle;
    white-space: nowrap;
  `;
const fixedStyles = ({ theme, fixed }) => fixed &&
    css `
    ${theme.mq.untilMega} {
      left: 0;
      position: sticky;
      width: 145px;
      overflow-wrap: break-word;
      z-index: ${theme.zIndex.absolute};

      &:after {
        content: '';
        background: linear-gradient(
          90deg,
          rgba(0, 0, 0, 0.12),
          rgba(255, 255, 255, 0)
        );
        height: 100%;
        position: absolute;
        top: 0;
        left: 100%;
        width: 6px;
      }
    }
  `;
const sortableStyles = ({ sortable }) => sortable &&
    css `
    cursor: pointer;
    position: relative;
    user-select: none;

    &:focus-within::after {
      content: '';
      display: block;
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 1;
      ${focusOutline()};
    }

    &:focus-within,
    &:hover {
      background-color: var(--cui-bg-normal-hovered);
      color: var(--cui-fg-accent-hovered);

      & > button {
        opacity: 1;
      }
    }

    &:active {
      background-color: var(--cui-bg-normal-pressed);
      color: var(--cui-fg-accent-pressed);
    }
  `;
const sortableActiveStyles = ({ sortable, isSorted }) => sortable &&
    isSorted &&
    css `
    & > button {
      opacity: 1;
    }
  `;
const condensedStyles = ({ condensed, theme }) => condensed &&
    css `
    ${typography('two')(theme)};
    vertical-align: middle;
    padding: ${theme.spacings.kilo} ${theme.spacings.mega}
      ${theme.spacings.kilo} ${theme.spacings.giga};
  `;
const condensedColStyles = ({ condensed, scope, theme, }) => condensed &&
    scope === 'col' &&
    css `
    padding: ${theme.spacings.byte} ${theme.spacings.mega}
      ${theme.spacings.byte} ${theme.spacings.giga};
  `;
const StyledHeader = styled('th', {
    shouldForwardProp: (prop) => isPropValid(prop),
})(baseStyles, hoveredStyles, fixedStyles, colStyles, sortableStyles, sortableActiveStyles, condensedStyles, condensedColStyles);
/**
 * TableHeader for the Table component. The Table handles rendering it.
 */
const TableHeader = (_a) => {
    var { children, condensed, align = 'left', scope = 'col', fixed = false, isHovered = false, sortParams = { sortable: false }, onClick } = _a, props = __rest(_a, ["children", "condensed", "align", "scope", "fixed", "isHovered", "sortParams", "onClick"]);
    if (process.env.NODE_ENV !== 'production' &&
        process.env.NODE_ENV !== 'test' &&
        sortParams.sortable &&
        !sortParams.sortLabel) {
        throw new AccessibilityError('Table', 'The `sortLabel` prop is missing. Omit the `sortable` prop if you intend to disable the row sorting functionality.');
    }
    return (_jsxs(StyledHeader, Object.assign({ condensed: condensed, align: align, scope: scope, fixed: fixed, isHovered: isHovered, sortable: sortParams.sortable, isSorted: !!sortParams.isSorted, "aria-label": sortParams.sortLabel, "aria-sort": sortParams.sortable ? sortParams.sortDirection || 'none' : undefined, onClick: onClick }, props, { children: [sortParams.sortable && (_jsx(SortArrow, { label: sortParams.sortLabel, direction: sortParams.sortDirection, onClick: onClick })), children] })));
};
export default TableHeader;
